// AI usage allowances ("haklar") — mirrors lib/models/ai_allowance_model.dart and
// UserService.getAiAllowance in the mobile app. The Cloud Functions enforce and
// count the AI kinds (fitviz_cloud/functions/ai_allowance.py); this only reads
// users/{uid}.aiUsage to show counters and block early. programSnapshot isn't
// an AI call, so it is still counted here, under users/{uid}.clientUsage.
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../../firebase';
import { dateKey, mondayOf } from './dates';

export const AI_KIND = {
  training: 'training',
  nutrition: 'nutrition',
  nutritionPhoto: 'nutritionPhoto',
  programAnalysis: 'programAnalysis',
  macroTargets: 'macroTargets',
  // Not AI, but each use writes Firestore docs: hidden daily cap against spam.
  programSnapshot: 'programSnapshot',
};

const isWeekly = (kind) => kind === AI_KIND.training;
const isMain = (kind) => kind === AI_KIND.training || kind === AI_KIND.nutrition || kind === AI_KIND.nutritionPhoto;
const usageField = (kind) => (kind === AI_KIND.programSnapshot ? 'clientUsage' : 'aiUsage');

export function periodKey(kind, now = new Date()) {
  return dateKey(isWeekly(kind) ? mondayOf(now) : now);
}

/** Base uses per period; null = unlimited. */
export function baseLimitFor(kind, subscription) {
  const sub = String(subscription || 't0').toLowerCase();
  if (sub === 'super' || sub === 'limitless') return null;
  if (sub === 't1') {
    return { training: 10, nutrition: 20, nutritionPhoto: 10, programAnalysis: 10, macroTargets: 5, programSnapshot: 20 }[kind];
  }
  return { training: 1, nutrition: 1, nutritionPhoto: 0, programAnalysis: 3, macroTargets: 3, programSnapshot: 5 }[kind];
}

/** One-time uses a free user gets on top of the period limit. */
export function welcomeUsesFor(kind, subscription) {
  if (String(subscription || 't0').toLowerCase() !== 't0') return 0;
  return kind === AI_KIND.nutrition || kind === AI_KIND.nutritionPhoto ? 3 : 0;
}

export function maxAdBonusFor(kind, subscription) {
  if (String(subscription || 't0').toLowerCase() !== 't0') return 0;
  return { training: 2, nutrition: 3 }[kind] || 0;
}

function readUsage(userData, kind) {
  const entry = userData?.[usageField(kind)]?.[kind];
  // Same rule as the server: a stored period at or after today's still counts.
  if (!entry || typeof entry.period !== 'string' || entry.period < periodKey(kind)) return { used: 0, adBonus: 0 };
  return { used: Number(entry.used) || 0, adBonus: Number(entry.adBonus) || 0 };
}

export function allowanceFromUserData(userData, kind) {
  const subscription = String(userData?.subscription || 't0').toLowerCase();
  const usage = readUsage(userData, kind);
  const base = baseLimitFor(kind, subscription);
  const limit = base == null ? null : base + usage.adBonus;
  const welcomeTotal = welcomeUsesFor(kind, subscription);
  const welcomeRemaining = Math.max(0, welcomeTotal - (Number(userData?.aiUsage?.welcome?.[kind]) || 0));
  // Uses won on the app's daily gift wheel; spent after period and welcome uses.
  const giftRemaining = Math.max(0, Number(userData?.aiUsage?.gift?.[kind]) || 0);
  const remaining = limit == null ? Infinity : Math.max(0, limit - usage.used) + welcomeRemaining + giftRemaining;
  return {
    kind,
    subscription,
    limit,
    used: usage.used,
    adBonus: usage.adBonus,
    welcomeRemaining,
    giftRemaining,
    remaining,
    canUse: remaining > 0,
    isUnlimited: limit == null,
    isVisibleToUser: isMain(kind) && subscription === 't0',
    canUnlockWithAd: remaining <= 0 && usage.adBonus < maxAdBonusFor(kind, subscription),
  };
}

export async function getAiAllowance(uid, kind) {
  try {
    const snap = await getDoc(doc(db, 'users', uid));
    return allowanceFromUserData(snap.data(), kind);
  } catch (e) {
    // Fails open, like the app: a Firestore hiccup never blocks the user.
    return allowanceFromUserData({ subscription: 'super' }, kind);
  }
}

/** Counts one programSnapshot use. AI kinds are counted by the server. */
export async function recordAiAllowanceUse(uid, kind) {
  if (kind !== AI_KIND.programSnapshot) throw new Error(`${kind} is counted by the server`);
  const ref = doc(db, 'users', uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) return;
    const usage = readUsage(snap.data(), kind);
    tx.update(ref, {
      [`${usageField(kind)}.${kind}`]: { period: periodKey(kind), used: usage.used + 1, adBonus: usage.adBonus },
    });
  });
}

/** Mirrors aiAllowanceBlockedMessage() in ai_allowance_card.dart (no ads on web). */
export function allowanceBlockedMessage(t, status) {
  if (!status.isVisibleToUser) {
    return isWeekly(status.kind) ? t('allowanceFairUseWeekly') : t('allowanceFairUseDaily');
  }
  if (status.kind === AI_KIND.nutritionPhoto) return `${t('allowancePhotoUsedUp')}. ${t('allowancePhotoPremiumHint')}`;
  const usedUp = status.kind === AI_KIND.training ? t('allowanceTrainingUsedUp') : t('allowanceNutritionUsedUp');
  const hint = status.kind === AI_KIND.training ? t('allowanceTrainingResets') : t('allowanceNutritionResets');
  return `${usedUp}. ${hint}`;
}
